import React, { useMemo, useState, useCallback } from 'react';
import {
  buildSvgPalette,
  buildFamilyRemap,
  isPaletteSlotEdited,
  colorToHex,
} from '../../lib/svgColorPalette';
import SvgGradientsEditor from './SvgGradientsEditor';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ChevronDown, ChevronRight } from 'lucide-react';

/**
 * Smart Color Palette editor — Canva-style 3-slot recolor UI for uploaded
 * SVGs.  Replaces the previous flat colorMap editor as the DEFAULT view, and
 * tucks the full per-color and per-gradient editors behind a collapsible
 * "Advanced SVG Colors" section.
 *
 * Behavior:
 *   • Palette swatches show Primary / Secondary / Accent.  Empty slots are
 *     hidden — small SVGs with two colors only render two swatches.
 *   • Editing a swatch:
 *       1. Computes a family-aware remap of every member's color, anchored
 *          on the family's ORIGINAL representative (not the previous user
 *          choice) so palette edits are idempotent in target.
 *       2. Merges the remap into the existing colorMap (preserving manual
 *          edits to colors outside the family).
 *       3. Writes both `colorMap` and `palette` in a single onUpdateElement
 *          call so there's no race or stale-state risk.
 *   • Advanced section: re-uses the original colorMap row editor (per-color
 *     picker + Reset + brand swatches) and the SvgGradientsEditor — power
 *     users still get every knob.
 *
 * Pure UI — no rendering logic.  Edits flow through the existing colorMap
 * pipeline, so gradient structure / stop positions / opacity / filters /
 * shadows are all preserved by the render layer's existing fidelity
 * guarantees.
 */

const SLOT_LABELS = { primary: 'Primary', secondary: 'Secondary', accent: 'Accent' };

const SvgPaletteEditor = ({ selectedElement, onUpdateElement, brandKit }) => {
  const { svgContent, colorMap = {}, palette: storedPalette = {}, id: elId } = selectedElement || {};

  // Build the palette from the ORIGINAL svgContent.  This is the stable
  // anchor — never depends on colorMap edits, so remaps are idempotent.
  const paletteData = useMemo(() => buildSvgPalette(svgContent || ''), [svgContent]);
  const { families, slots, defaults, colors } = paletteData;

  // Effective swatch values shown to the user — stored override, else default
  // representative.  Falsy slots (no such family) keep the slot hidden.
  const effectiveSwatches = {
    primary:   storedPalette.primary   ?? defaults.primary,
    secondary: storedPalette.secondary ?? defaults.secondary,
    accent:    storedPalette.accent    ?? defaults.accent,
  };

  const [showAdvanced, setShowAdvanced] = useState(false);

  // Apply a palette change atomically.
  const applySlotChange = useCallback(
    (slotName, newColor) => {
      const family = slots[slotName];
      if (!family) return;
      const remap = buildFamilyRemap(family, newColor);
      // Merge over current colorMap — manual edits to UNRELATED colors
      // survive; manual edits to colors INSIDE this family are overwritten by
      // the new palette mapping (intentional Canva-style behavior).
      const nextColorMap = { ...(colorMap || {}), ...remap };
      const nextPalette  = { ...(storedPalette || {}), [slotName]: newColor };
      onUpdateElement(elId, { colorMap: nextColorMap, palette: nextPalette });
    },
    [slots, colorMap, storedPalette, onUpdateElement, elId],
  );

  // Reset a single slot — wipe the user's override AND undo the family's
  // colorMap entries (so the family returns to its original colors).
  const resetSlot = useCallback(
    (slotName) => {
      const family = slots[slotName];
      if (!family) return;
      const nextColorMap = { ...(colorMap || {}) };
      // For each family member, set its colorMap entry back to identity (or
      // delete the entry — identity is safer, matches initial state from
      // parseSvgForEditor).
      for (const m of family.members) {
        nextColorMap[m.original] = m.original;
      }
      const nextPalette = { ...(storedPalette || {}) };
      delete nextPalette[slotName];
      onUpdateElement(elId, { colorMap: nextColorMap, palette: nextPalette });
    },
    [slots, colorMap, storedPalette, onUpdateElement, elId],
  );

  // Reset the entire palette
  const resetAll = useCallback(() => {
    const nextColorMap = { ...(colorMap || {}) };
    for (const slotName of Object.keys(SLOT_LABELS)) {
      const family = slots[slotName];
      if (!family) continue;
      for (const m of family.members) nextColorMap[m.original] = m.original;
    }
    onUpdateElement(elId, { colorMap: nextColorMap, palette: {} });
  }, [slots, colorMap, onUpdateElement, elId]);

  // Per-color (advanced) edit handler — same shape as the old flat block
  const editSingleColor = useCallback(
    (original, newColor) => {
      onUpdateElement(elId, { colorMap: { ...(colorMap || {}), [original]: newColor } });
    },
    [colorMap, onUpdateElement, elId],
  );

  // Nothing to do for SVGs that don't have a colorMap at all (Memphis tint)
  if (!colorMap || Object.keys(colorMap).length === 0) {
    // Caller may still render a separate Memphis "Fill Color" block — we just
    // bow out and let it do that.
    return null;
  }

  const anyPaletteEdit = Object.keys(SLOT_LABELS).some((s) =>
    isPaletteSlotEdited(storedPalette, s, slots[s]),
  );

  const slotEntries = Object.keys(SLOT_LABELS)
    .filter((slotName) => slots[slotName] && effectiveSwatches[slotName])
    .map((slotName) => ({
      slotName,
      label: SLOT_LABELS[slotName],
      family: slots[slotName],
      color: effectiveSwatches[slotName],
      edited: isPaletteSlotEdited(storedPalette, slotName, slots[slotName]),
    }));

  return (
    <>
      {/* ── PALETTE HEADER ────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 py-2">
        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">
          Palette
        </span>
        <div className="flex-1 h-px bg-gray-100" />
        {anyPaletteEdit && (
          <button
            type="button"
            onClick={resetAll}
            className="text-[9px] text-gray-400 hover:text-purple-500 transition-colors"
            title="Reset every slot to the SVG's original colors"
          >
            Reset all
          </button>
        )}
      </div>

      {/* ── PALETTE SWATCHES ──────────────────────────────────────────── */}
      {slotEntries.length === 0 ? (
        <p className="text-[10px] text-gray-400 leading-relaxed pb-1">
          This SVG doesn't expose recolor-able palette colors (likely all neutrals or pattern fills).
        </p>
      ) : (
        <div className="space-y-2.5 pb-1">
          {slotEntries.map(({ slotName, label, family, color, edited }) => (
            <div key={slotName} className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">
                  {label}
                </Label>
                <span
                  className="text-[9px] text-gray-400"
                  title={`${family.members.length} color${family.members.length === 1 ? '' : 's'} in this family`}
                >
                  {family.members.length} shade{family.members.length === 1 ? '' : 's'}
                </span>
              </div>
              <div className="flex items-center gap-2 min-w-0">
                <input
                  type="color"
                  value={safeHex(color)}
                  onChange={(e) => applySlotChange(slotName, e.target.value)}
                  className="w-8 h-8 rounded border cursor-pointer p-0.5 flex-shrink-0"
                  data-testid={`palette-swatch-${slotName}`}
                />
                <Input
                  type="text"
                  value={safeHex(color)}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (/^#[0-9a-fA-F]{6}$/.test(v)) applySlotChange(slotName, v);
                  }}
                  className="flex-1 min-w-0 h-8 text-xs font-mono"
                  maxLength={7}
                />
                {edited && (
                  <button
                    type="button"
                    onClick={() => resetSlot(slotName)}
                    title="Restore this slot to the SVG's original color"
                    className="text-[9px] text-gray-400 hover:text-purple-500 whitespace-nowrap flex-shrink-0 transition-colors"
                  >
                    Reset
                  </button>
                )}
              </div>
              {brandKit?.colors?.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-0.5">
                  {brandKit.colors.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className="w-5 h-5 rounded border-2 border-transparent hover:border-purple-400 transition-colors"
                      style={{ background: c.hex }}
                      title={c.name}
                      onClick={() => applySlotChange(slotName, c.hex)}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── ADVANCED (collapsible) ────────────────────────────────────── */}
      <button
        type="button"
        onClick={() => setShowAdvanced((v) => !v)}
        className="w-full flex items-center gap-1.5 py-2 text-[10px] font-semibold uppercase tracking-widest text-gray-400 hover:text-gray-600 transition-colors"
        data-testid="svg-advanced-toggle"
      >
        {showAdvanced ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        Advanced SVG Colors
        <span className="ml-auto text-[9px] text-gray-400 normal-case tracking-normal">
          {colors.length} color{colors.length === 1 ? '' : 's'}
        </span>
      </button>

      {showAdvanced && (
        <div className="space-y-3 pb-1">
          {/* Per-color editor — same UI shape as the old flat block */}
          <p className="text-[10px] text-gray-400 leading-relaxed">
            Edit each detected color individually.  Changes here override palette mappings for the specific color you touch.
          </p>
          {Object.entries(colorMap).map(([original, current], idx) => (
            <div key={original} className="space-y-1">
              <Label className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">
                {original}
              </Label>
              <div className="flex items-center gap-2 min-w-0">
                <input
                  type="color"
                  value={safeHex(current)}
                  onChange={(e) => editSingleColor(original, e.target.value)}
                  className="w-7 h-7 rounded border cursor-pointer p-0.5 flex-shrink-0"
                />
                <Input
                  type="text"
                  value={current}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (/^#[0-9a-fA-F]{6}$/.test(v)) editSingleColor(original, v);
                  }}
                  className="flex-1 min-w-0 h-7 text-[11px] font-mono"
                  maxLength={7}
                />
                {current !== original && (
                  <button
                    type="button"
                    title="Reset to original"
                    onClick={() => editSingleColor(original, original)}
                    className="text-[9px] text-gray-400 hover:text-purple-500 whitespace-nowrap flex-shrink-0 transition-colors"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Gradient inspector */}
          <SvgGradientsEditor
            svgContent={svgContent}
            gradientEdits={selectedElement.gradientEdits}
            onChange={(edits) => onUpdateElement(elId, { gradientEdits: edits })}
          />
        </div>
      )}
    </>
  );
};

// <input type="color"> insists on "#rrggbb".  Coerce any incoming color form
// (named, rgb, short hex) without losing the underlying stored value.
function safeHex(c) {
  const h = colorToHex(c);
  return h || '#000000';
}

export default SvgPaletteEditor;
